import React, { useEffect, useRef, useState } from 'react';
import { BRAND_NAME } from '../brand';

import { effectiveUserTimeZone } from '../utils/userTime';
import type { TodayCalendarEvent, TodayCalendarState } from '../api/todayCalendar';

const STRIP_HEIGHT_CLASS = 'h-[5.5rem]';
const WORK_ITEM_DUE_PREFIX = 'work-item:';

type TodayCalendarStripProps = {
  state: TodayCalendarState | 'loading';
  events: TodayCalendarEvent[];
  timeZone?: string | null;
  onOpenDue?: (workItemId: string, activator: HTMLElement) => void;
};

export function dueWorkItemId(event: TodayCalendarEvent): string | null {
  if (!event.id.startsWith(WORK_ITEM_DUE_PREFIX)) return null;
  const id = event.id.slice(WORK_ITEM_DUE_PREFIX.length).trim();
  return id || null;
}

function formatEventTime(event: TodayCalendarEvent, timeZone: string): string {
  if (event.all_day) return 'All day';
  const instant = new Date(event.start);
  if (Number.isNaN(instant.getTime())) return '';
  return new Intl.DateTimeFormat(undefined, {
    timeZone,
    hour: 'numeric',
    minute: '2-digit',
  }).format(instant);
}

function NotConnectedCopy() {
  return (
    <div className="min-w-0 max-w-[11rem] shrink px-3 flex flex-col justify-center">
      <p className="text-sm font-semibold leading-snug text-theme m-0">Outside calendar not linked. Your {BRAND_NAME} dues still show here.</p>
    </div>
  );
}

function ChipCopy({
  event,
  timeZone,
  quiet,
  origin,
}: {
  event: TodayCalendarEvent;
  timeZone: string;
  quiet: boolean;
  origin?: string;
}) {
  return (
    <>
      <div className="flex items-baseline gap-1.5 min-w-0">
        <p
          data-testid="today-calendar-event-time"
          className={
            quiet
              ? 'text-sm font-medium leading-tight text-theme-secondary m-0'
              : 'text-lg font-semibold leading-tight text-theme m-0'
          }
        >
          {formatEventTime(event, timeZone)}
        </p>
        {origin ? (
          <p
            data-testid="today-calendar-event-origin"
            className="text-[10px] leading-tight text-theme-muted m-0"
          >
            {origin}
          </p>
        ) : null}
      </div>
      <p
        data-testid="today-calendar-event-title"
        className={
          quiet
            ? 'text-[11px] text-theme-muted m-0 mt-1 whitespace-nowrap overflow-hidden text-ellipsis'
            : 'text-xs text-theme-secondary m-0 mt-1 whitespace-nowrap overflow-hidden text-ellipsis'
        }
        title={event.title}
      >
        {event.title}
      </p>
    </>
  );
}

function EventRow({
  events,
  timeZone,
  scrollerRef,
  overflows,
  onOpenDue,
}: {
  events: TodayCalendarEvent[];
  timeZone: string;
  scrollerRef: React.RefObject<HTMLDivElement | null>;
  overflows: boolean;
  onOpenDue?: (workItemId: string, activator: HTMLElement) => void;
}) {
  return (
    <div className="relative h-full min-w-0 flex-1">
      <div
        ref={scrollerRef}
        className="h-full overflow-x-auto overflow-y-hidden px-3 flex items-stretch gap-3"
      >
        {events.map((event) => {
          const workItemId = dueWorkItemId(event);
          if (workItemId && event.all_day) {
            return (
              <button
                key={event.id}
                type="button"
                data-testid="today-calendar-due"
                data-work-item-id={workItemId}
                data-origin="domayn-due"
                className="shrink-0 w-36 max-w-[9rem] py-2 flex flex-col justify-center min-w-0 text-left appearance-none bg-transparent border-0 cursor-pointer rounded-lg focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
                style={{ outlineColor: 'var(--color-accent)' }}
                onClick={(click) => onOpenDue?.(workItemId, click.currentTarget)}
              >
                <ChipCopy event={event} timeZone={timeZone} quiet />
              </button>
            );
          }
          if (workItemId) {
            return (
              <button
                key={event.id}
                type="button"
                data-testid="today-calendar-scheduled"
                data-work-item-id={workItemId}
                data-origin="domayn-scheduled"
                className="shrink-0 w-36 max-w-[9rem] py-2 flex flex-col justify-center min-w-0 text-left appearance-none bg-transparent border-0 cursor-pointer rounded-lg focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
                style={{ outlineColor: 'var(--color-accent)' }}
                onClick={(click) => onOpenDue?.(workItemId, click.currentTarget)}
              >
                <ChipCopy event={event} timeZone={timeZone} quiet={false} origin="Scheduled" />
              </button>
            );
          }
          return (
            <article
              key={event.id}
              data-testid="today-calendar-event"
              className="shrink-0 w-40 max-w-[10rem] py-2 flex flex-col justify-center min-w-0"
            >
              <ChipCopy event={event} timeZone={timeZone} quiet={false} />
            </article>
          );
        })}
      </div>
      {overflows ? (
        <div
          data-testid="today-calendar-fade"
          className="pointer-events-none absolute inset-y-0 right-0 w-12"
          style={{
            background: 'linear-gradient(to left, var(--color-bg-primary), transparent)',
          }}
          aria-hidden="true"
        />
      ) : null}
    </div>
  );
}

const TodayCalendarStrip: React.FC<TodayCalendarStripProps> = ({
  state,
  events,
  timeZone,
  onOpenDue,
}) => {
  const scrollerRef = useRef<HTMLDivElement | null>(null);
  const [overflows, setOverflows] = useState(false);
  const zone = effectiveUserTimeZone(timeZone);
  const hasEvents = events.length > 0;

  useEffect(() => {
    const node = scrollerRef.current;
    if (!node || !hasEvents) {
      setOverflows(false);
      return;
    }
    const update = () => {
      setOverflows(node.scrollWidth > node.clientWidth + 1);
    };
    update();
    const observer = new ResizeObserver(update);
    observer.observe(node);
    return () => observer.disconnect();
  }, [state, events, hasEvents]);

  let body: React.ReactNode = null;
  if (state === 'loading') {
    body = null;
  } else if (state === 'not_connected' && !hasEvents) {
    body = (
      <div className={`px-4 ${STRIP_HEIGHT_CLASS} flex flex-col justify-center`}>
        <p className="text-sm font-semibold text-theme m-0">Outside calendar not linked. Your {BRAND_NAME} dues still show here.</p>
      </div>
    );
  } else if (state === 'not_connected') {
    body = (
      <div className={`h-full min-w-0 flex items-stretch`}>
        <NotConnectedCopy />
        <EventRow
          events={events}
          timeZone={zone}
          scrollerRef={scrollerRef}
          overflows={overflows}
          onOpenDue={onOpenDue}
        />
      </div>
    );
  } else if (state === 'empty' && !hasEvents) {
    body = (
      <div className={`px-4 ${STRIP_HEIGHT_CLASS} flex items-center`}>
        <p className="text-sm text-theme m-0" data-testid="today-calendar-empty">
          No meetings or {BRAND_NAME} dues on the strip for today.
        </p>
      </div>
    );
  } else if (hasEvents) {
    body = (
      <EventRow
        events={events}
        timeZone={zone}
        scrollerRef={scrollerRef}
        overflows={overflows}
        onOpenDue={onOpenDue}
      />
    );
  }

  return (
    <section
      className={`relative mb-4 ${STRIP_HEIGHT_CLASS} rounded-2xl border border-theme overflow-hidden`}
      style={{ backgroundColor: 'var(--color-bg-card)' }}
      data-testid="today-calendar-strip"
      aria-label="Today’s meetings and dues"
    >
      {body}
    </section>
  );
};

export default TodayCalendarStrip;
